import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Search, Eye, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RequestDetailsModal from '../components/admin/RequestDetailsModal';
import { sendNotification } from '../utils/notifications';

interface PriceRequest {
  id: string;
  userId: string;
  userName: string;
  companyName: string;
  productId?: string;
  productName?: string;
  baselinker_id?: string;
  marketplace?: string;
  currentPrice?: number;
  newPrice?: number;
  message: string;
  status: 'pending' | 'completed';
  createdAt: string;
  completedAt?: string;
  completionNote?: string;
  type: 'price' | 'domain';
  customDomain?: string;
}

export default function PriceRequests() {
  const [requests, setRequests] = useState<PriceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<PriceRequest | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadRequests();
    } else {
      navigate('/');
    }
  }, [currentUser]);

  const loadRequests = async () => {
    try {
      const requestsQuery = query(
        collection(db, 'price_requests'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(requestsQuery);
      const requestsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PriceRequest[];
      
      setRequests(requestsData);
    } catch (err) {
      console.error('Error loading price requests:', err);
      setError('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsCompleted = async (requestId: string, completionNote: string) => {
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) {
        console.error('Request not found:', requestId);
        setError('Erro: Pedido não encontrado');
        return;
      }

      console.log('Updating request:', request); // Debug log

      // Atualizar o status no Firebase
      await updateDoc(doc(db, 'price_requests', requestId), {
        status: 'completed',
        completedAt: new Date().toISOString(),
        completionNote
      });

      // Enviar notificação para o usuário
      try {
        console.log('Sending notification to user:', request.userId); // Debug log
        await sendNotification(request.userId, {
          title: request.type === 'domain' ? 'Pedido de Domínio Atualizado' : 'Pedido de Preço Atualizado',
          content: request.type === 'domain' 
            ? `Seu pedido de domínio personalizado (${request.customDomain}) foi processado.`
            : `Seu pedido de alteração de preço para o produto "${request.productName}" foi processado.`,
          type: 'success',
          link: '/dashboard/settings/price-requests'
        });
        console.log('Notification sent successfully'); // Debug log
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }

      // Recarregar os pedidos e fechar o modal
      await loadRequests();
      setSelectedRequest(null);
    } catch (err) {
      console.error('Error updating request:', err);
      setError('Erro ao atualizar pedido');
    }
  };

  const filteredRequests = requests.filter(request =>
    request.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.baselinker_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.customDomain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Pedidos de Clientes</h1>
          <p className="mt-2 text-sm text-gray-600">
            Lista de pedidos de alteração de preços e domínios personalizados dos clientes.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Pesquisar por produto, ID BaseLinker, empresa ou domínio..."
                className="block w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="w-32 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th scope="col" className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th scope="col" className="w-48 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produto
                  </th>
                  <th scope="col" className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID BaseLinker
                  </th>
                  <th scope="col" className="w-32 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Marketplace
                  </th>
                  <th scope="col" className="w-24 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preço Atual
                  </th>
                  <th scope="col" className="w-24 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Novo Preço
                  </th>
                  <th scope="col" className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Domínio
                  </th>
                  <th scope="col" className="w-24 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="w-20 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 text-sm text-gray-600">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="px-3 py-4 text-sm font-medium text-gray-900 truncate" title={request.companyName}>
                      {request.companyName}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 truncate" title={request.productName || '-'}>
                      {request.productName || '-'}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-600 truncate" title={request.baselinker_id || '-'}>
                      {request.baselinker_id || '-'}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-600 truncate" title={request.marketplace || '-'}>
                      {request.marketplace || '-'}
                    </td>
                    <td className="px-3 py-4 text-sm text-right text-gray-900">
                      {request.currentPrice ? `€${request.currentPrice.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-3 py-4 text-sm text-right text-gray-900">
                      {request.newPrice ? `€${request.newPrice.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 truncate" title={request.customDomain || '-'}>
                      {request.customDomain || '-'}
                    </td>
                    <td className="px-3 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize
                        ${request.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {request.status === 'completed' ? 'Efetuado' : 'Pendente'}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-right">
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={() => setSelectedRequest(request)}
                          className="inline-flex items-center p-1 border border-transparent rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {request.status === 'pending' && (
                          <button
                            onClick={() => handleMarkAsCompleted(request.id, '')}
                            className="inline-flex items-center p-1 border border-transparent rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedRequest && (
        <RequestDetailsModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onComplete={handleMarkAsCompleted}
        />
      )}
    </div>
  );
}